import { z } from "zod";
import type { ModelErrorType } from "../../shared/errors";
import {
  argumentsText,
  contentText,
  errorCode,
  finiteNumber,
  isRecord,
  jsonObject,
  jsonObjects,
  nonEmptyString,
  readableText,
  records,
} from "../../shared/json-body";
import {
  ALIBABA_BEIJING,
  ALIBABA_SINGAPORE,
  ALIBABA_VIRGINIA,
  canonicalModelName,
} from "../../shared/models";
import { type RoutingFacts, skuById, skuIdOf } from "../../shared/pricing";
import { encryptedReasoningPart, toolPart } from "../../shared/prompt";
import type {
  DeclaredTool,
  ModelPrompt,
  ModelReply,
  ModelRequestSettings,
  ModelToolCall,
  ModelTransportDefinition,
  OutputContract,
  PromptPart,
  StreamChunk,
} from "../../shared/transport";
import type { CacheWrite, ModelUsage } from "../../shared/usage";

const ANTHROPIC_VERSION = "2023-06-01";
/** The most output a request may ask for when its SKU names no limit. */
const MAX_OUTPUT_TOKENS = 64_000;
/**
 * Model Studio screens every prompt and reply and refuses a call it flags with `DataInspectionFailed`,
 * which a game set in real-world politics can trip. This header turns the screening off.
 */
const DATA_INSPECTION_HEADER = "x-dashscope-datainspection";
const INSPECTION_OFF = JSON.stringify({ input: "disable", output: "disable" });
/** Model Studio's explicit cache keeps a prompt five minutes, and has no longer lifetime. */
const FIVE_MINUTES = 300;

/**
 * Each Model Studio region Pax calls: its region ID, which the workspace domain names, the
 * workspace Pax calls it through, that workspace's key, and whether Alibaba lets Pax's account turn
 * off its content inspection there. Where it doesn't, the header is refused as `AccessDenied`.
 */
const alibabaRegions = {
  singapore: {
    region: ALIBABA_SINGAPORE,
    account: "ALIBABA_WORKSPACE_ID_SINGAPORE",
    credential: "ALIBABA_API_KEY_SINGAPORE",
    inspectionOff: true,
  },
  virginia: {
    region: ALIBABA_VIRGINIA,
    account: "ALIBABA_WORKSPACE_ID_VIRGINIA",
    credential: "ALIBABA_API_KEY_VIRGINIA",
    inspectionOff: false,
  },
  beijing: {
    region: ALIBABA_BEIJING,
    account: "ALIBABA_WORKSPACE_ID_BEIJING",
    credential: "ALIBABA_API_KEY_BEIJING",
    inspectionOff: true,
  },
} as const;

export type AlibabaRegion = keyof typeof alibabaRegions;
export const alibabaRegionNames = Object.keys(alibabaRegions) as [
  AlibabaRegion,
  ...AlibabaRegion[],
];

export const AlibabaRegionParams = z.strictObject({
  region: z.enum(alibabaRegionNames).meta({
    description: "The Model Studio region, which picks Alibaba's endpoint, key, and prices.",
    example: "singapore",
  }),
});
export type AlibabaRegionParams = z.infer<typeof AlibabaRegionParams>;

const cacheControl = z
  .strictObject({ type: z.literal("ephemeral"), ttl: z.literal("5m").optional() })
  .nullable()
  .optional();

const TextBlock = z.strictObject({
  type: z.literal("text"),
  text: z.string(),
  cache_control: cacheControl,
});

const ImageBlock = z.strictObject({
  type: z.literal("image"),
  source: z.discriminatedUnion("type", [
    z.strictObject({
      type: z.literal("base64"),
      media_type: z.enum(["image/jpeg", "image/png", "image/gif", "image/webp"]),
      data: z.string(),
    }),
    z.strictObject({ type: z.literal("url"), url: z.url() }),
  ]),
  cache_control: cacheControl,
});

const ContentBlock = z.discriminatedUnion("type", [
  TextBlock,
  ImageBlock,
  /** Qwen signs its thinking with an empty signature, which is sent back as it came. */
  z.strictObject({ type: z.literal("thinking"), thinking: z.string(), signature: z.string() }),
  z.strictObject({
    type: z.literal("tool_use"),
    id: z.string(),
    name: z.string(),
    input: z.record(z.string(), z.unknown()),
    cache_control: cacheControl,
  }),
  z.strictObject({
    type: z.literal("tool_result"),
    tool_use_id: z.string(),
    content: z
      .union([z.string(), z.array(z.discriminatedUnion("type", [TextBlock, ImageBlock]))])
      .optional(),
    is_error: z.boolean().optional(),
    cache_control: cacheControl,
  }),
]);

const Message = z.strictObject({
  role: z.enum(["user", "assistant"]),
  content: z.union([z.string(), z.array(ContentBlock)]),
});

/** Only tools the caller runs: Model Studio runs none of Anthropic's server or built-in tools. */
const Tool = z.strictObject({
  type: z.literal("custom").nullable().optional(),
  name: z.string().regex(/^[a-zA-Z0-9_-]{1,128}$/),
  description: z.string().optional(),
  input_schema: z.looseObject({ type: z.literal("object") }),
  cache_control: cacheControl,
  strict: z.boolean().optional(),
  eager_input_streaming: z.boolean().nullable().optional(),
});

const disableParallelToolUse = z.boolean().optional();

const ToolChoice = z.discriminatedUnion("type", [
  z.strictObject({ type: z.literal("auto"), disable_parallel_tool_use: disableParallelToolUse }),
  z.strictObject({ type: z.literal("any"), disable_parallel_tool_use: disableParallelToolUse }),
  z.strictObject({
    type: z.literal("tool"),
    name: z.string(),
    disable_parallel_tool_use: disableParallelToolUse,
  }),
  z.strictObject({ type: z.literal("none") }),
]);

const Thinking = z.discriminatedUnion("type", [
  z.strictObject({ type: z.literal("enabled"), budget_tokens: z.number().int().min(1024) }),
  z.strictObject({ type: z.literal("disabled") }),
]);

export const AlibabaMessagesRequest = z
  .strictObject({
    model: z.string().min(1).meta({ example: "qwen3.8-max" }),
    max_tokens: z.number().int().positive().meta({
      description: "Required. Caps thinking and the answer together, at most the model's limit.",
      example: 8192,
    }),
    messages: z.array(Message),
    system: z.union([z.string(), z.array(TextBlock)]).optional(),
    stream: z.boolean().optional(),
    stop_sequences: z.array(z.string()).optional(),
    temperature: z.number().min(0).max(1).optional(),
    top_k: z.number().int().nonnegative().optional(),
    top_p: z.number().min(0).max(1).optional(),
    tools: z.array(Tool).optional(),
    tool_choice: ToolChoice.optional(),
    thinking: Thinking.optional(),
    output_config: z
      .strictObject({
        effort: z.enum(["low", "medium", "high", "xhigh", "max"]).nullable().optional(),
        format: z
          .strictObject({
            type: z.literal("json_schema"),
            schema: z.record(z.string(), z.unknown()),
          })
          .nullable()
          .optional(),
      })
      .optional(),
  })
  .meta({
    id: "AlibabaMessagesRequest",
    description:
      "An Anthropic Messages request as Model Studio accepts it: text, images, thinking, and the caller's own tools. Anthropic's server tools, built-in tools, documents, and betas are refused.",
    example: {
      model: "qwen3.8-max",
      max_tokens: 8192,
      messages: [{ role: "user", content: "Who holds Burgundy in 1444?" }],
    },
  });
export type AlibabaMessagesRequest = z.infer<typeof AlibabaMessagesRequest>;

export const AlibabaMessage = z
  .looseObject({
    id: z.string(),
    type: z.literal("message"),
    model: z.string(),
    usage: z.looseObject({}),
  })
  .meta({
    id: "AlibabaMessage",
    description:
      "Alibaba's response, byte for byte, in Anthropic's message format: a message, or its server-sent events when `stream` is true.",
  });

/**
 * Model Studio's Anthropic-compatible Messages API in one region, through the workspace's own
 * domain, which takes only that workspace's keys, and billed at the region's SKUs. Alibaba's
 * replies don't say where the model ran; the workspace's region is where the call's data is kept.
 */
function alibabaMessages(name: AlibabaRegion): ModelTransportDefinition<AlibabaMessagesRequest> {
  const { region, account, credential, inspectionOff } = alibabaRegions[name];
  const requestedFacts = (model: string | undefined): Partial<RoutingFacts> => ({
    ...(model === undefined ? {} : { model }),
    tier: "standard",
    dataLocation: region,
  });
  const skuOf = ({ model, tier }: Partial<RoutingFacts>) =>
    model === undefined || tier === undefined
      ? undefined
      : skuIdOf({ billedBy: "alibaba", model: canonicalModelName(model), endpoint: name, tier });
  return {
    vendor: "alibaba",
    provider: "alibaba",
    acceptsPath: path => path === `${name}/apps/anthropic/v1/messages`,
    upstreamUrl: (_path, workspace) =>
      `https://${workspace}.${region}.maas.aliyuncs.com/apps/anthropic/v1/messages`,
    account,
    credential,
    authorize: (headers, key) => {
      headers.set("x-api-key", key);
      if (!headers.has("anthropic-version")) headers.set("anthropic-version", ANTHROPIC_VERSION);
      if (inspectionOff) headers.set(DATA_INSPECTION_HEADER, INSPECTION_OFF);
    },
    request: AlibabaMessagesRequest,
    headers: {
      "content-type": z.string().regex(/^application\/json\b/),
      accept: z.string(),
      "anthropic-version": z.literal(ANTHROPIC_VERSION),
    },
    query: {},
    billedBy: "alibaba",
    readRequestedFacts: request => requestedFacts(nonEmptyString(jsonObject(request.body).model)),
    skuOf,
    refusal: (_path, request) => {
      const sku = skuById(skuOf(requestedFacts(request.model)));
      if (!sku) {
        return {
          refusal: "model_not_found",
          message: `Pax has no price for ${request.model} in Alibaba's ${name} region.`,
        };
      }
      const limit = sku.maxOutputTokens ?? MAX_OUTPUT_TOKENS;
      if (request.max_tokens > limit) {
        return {
          refusal: "invalid_request",
          message: `max_tokens is ${request.max_tokens}, above ${request.model}'s limit of ${limit}.`,
        };
      }
      return null;
    },
    readModel: (_path, requestBody) => nonEmptyString(jsonObject(requestBody).model),
    readUsage,
    readSettings: (_path, requestBody) => readSettings(requestBody),
    readPrompt,
    readReply,
    readChunks,
    readOutputContract: (_path, requestBody) => readOutputContract(requestBody),
    errorTypeOf,
  };
}

export const alibabaSingaporeMessages = alibabaMessages("singapore");
export const alibabaVirginiaMessages = alibabaMessages("virginia");
export const alibabaBeijingMessages = alibabaMessages("beijing");

/**
 * Model Studio's own error codes by their family, so `Throttling.RateQuota` reads as `Throttling`,
 * then the Anthropic error types it answers in for what it checks the Anthropic way.
 */
const alibabaErrorTypes: Record<string, ModelErrorType> = {
  InvalidApiKey: "auth",
  AccessDenied: "auth",
  Arrearage: "billing",
  Throttling: "rate_limited",
  InvalidParameter: "invalid_request",
  DataInspectionFailed: "invalid_request",
  ModelNotFound: "model_not_found",
  InternalError: "server_error",
  RequestTimeOut: "timeout",
  overloaded_error: "overloaded",
  api_error: "server_error",
  rate_limit_error: "rate_limited",
  authentication_error: "auth",
  permission_error: "auth",
  not_found_error: "model_not_found",
  invalid_request_error: "invalid_request",
};

/** Model Studio says a model it doesn't serve in the region is an `InvalidParameter`. */
const MODEL_NOT_SERVED = /model not exist/i;

function errorTypeOf(
  code: string | undefined,
  message: string | undefined,
): ModelErrorType | undefined {
  if (!code) return undefined;
  if (message && MODEL_NOT_SERVED.test(message)) return "model_not_found";
  return alibabaErrorTypes[code] ?? alibabaErrorTypes[code.split(".")[0] ?? code];
}

function readOutputContract(requestBody: string): OutputContract {
  const body = jsonObject(requestBody);
  const output = isRecord(body.output_config) ? body.output_config : {};
  const format = isRecord(output.format) ? output.format : {};
  const tools = new Map<string, DeclaredTool>();
  for (const tool of records(body.tools)) {
    const name = nonEmptyString(tool.name);
    if (name) tools.set(name, { json: true, schema: tool.input_schema });
  }
  return {
    tools,
    openTools: false,
    json: format.type === "json_schema",
    responseSchema: format.type === "json_schema" ? format.schema : undefined,
  };
}

/**
 * A response's usage, merged across the `message_start` and `message_delta` events of a stream.
 * `input_tokens` leaves out cache reads and writes, which are added back here.
 *
 * `cache_read_input_tokens` holds hits on both the implicit cache and the explicit one
 * `cache_control` asks for, which are priced apart. Only a call that used the explicit cache
 * reports `cache_creation`, so a hit without it came from the implicit cache.
 */
function readUsage(body: string): ModelUsage | undefined {
  let model: string | undefined;
  const usage: Record<string, unknown> = {};
  for (const event of jsonObjects(body)) {
    const message = isRecord(event.message) ? event.message : event;
    model = nonEmptyString(message.model) ?? model;
    for (const source of [message.usage, event.usage])
      if (isRecord(source)) Object.assign(usage, definedFields(source));
  }
  const uncached = finiteNumber(usage.input_tokens);
  const outputTokens = finiteNumber(usage.output_tokens);
  if (uncached === undefined && outputTokens === undefined) return;
  const read = finiteNumber(usage.cache_read_input_tokens) ?? 0;
  const explicit = isRecord(usage.cache_creation);
  const cacheWrites = readCacheWrites(usage);
  const written = cacheWrites.reduce((sum, write) => sum + write.tokens, 0);
  return {
    model,
    inputTokens: (uncached ?? 0) + read + written,
    audioInputTokens: 0,
    implicitCacheReadTokens: explicit ? 0 : read,
    explicitCacheReadTokens: explicit ? read : 0,
    cacheWrites,
    outputTokens: outputTokens ?? 0,
    reasoningTokens: 0,
    toolUses: {},
  };
}

const definedFields = (source: Record<string, unknown>) =>
  Object.fromEntries(Object.entries(source).filter(([, value]) => value != null));

function readCacheWrites(usage: Record<string, unknown>): CacheWrite[] {
  const byLifetime = isRecord(usage.cache_creation) ? usage.cache_creation : undefined;
  const tokens =
    finiteNumber(byLifetime?.ephemeral_5m_input_tokens) ??
    finiteNumber(usage.cache_creation_input_tokens) ??
    0;
  return tokens ? [{ tokens, ttlSeconds: FIVE_MINUTES }] : [];
}

function readSettings(requestBody: string): ModelRequestSettings {
  const body = jsonObject(requestBody);
  const thinking = isRecord(body.thinking) ? body.thinking : {};
  const output = isRecord(body.output_config) ? body.output_config : {};
  const format = isRecord(output.format) ? output.format : {};
  return {
    model: nonEmptyString(body.model),
    maxTokens: finiteNumber(body.max_tokens),
    temperature: finiteNumber(body.temperature),
    topP: finiteNumber(body.top_p),
    stream: body.stream === true,
    reasoningLevel: nonEmptyString(output.effort) ?? nonEmptyString(thinking.type),
    outputType: format.type === "json_schema" ? "json" : undefined,
  };
}

/** Tools, then the system prompt, then the messages: the order Anthropic's format caches prompts in. */
function readPrompt(requestBody: string): ModelPrompt {
  const body = jsonObject(requestBody);
  const tools = records(body.tools).map(toolPart);
  const system = typeof body.system === "string" ? [{ text: body.system }] : records(body.system);
  const conversation: PromptPart[] = [];
  const toolResults = new Map<string, string>();
  for (const message of records(body.messages)) {
    const blocks =
      typeof message.content === "string"
        ? [{ type: "text", text: message.content }]
        : records(message.content);
    const assistant = message.role === "assistant";
    for (const block of blocks) {
      const part = blockPart(block, assistant, toolResults);
      if (part) conversation.push(part);
    }
  }
  return {
    parts: [
      ...tools,
      ...system.map(block => {
        const text = nonEmptyString(block.text) ?? "";
        return { role: "system" as const, label: "System", text, sent: text };
      }),
      ...conversation,
    ],
    toolResults,
  };
}

function blockPart(
  block: Record<string, unknown>,
  assistant: boolean,
  toolResults: Map<string, string>,
): PromptPart | undefined {
  const type = nonEmptyString(block.type) ?? "text";
  if (type === "text") {
    const text = nonEmptyString(block.text) ?? "";
    return assistant
      ? { role: "assistant", label: "Assistant", text, sent: text }
      : { role: "user", label: "User", text, sent: text };
  }
  if (type === "thinking") {
    const thinking = nonEmptyString(block.thinking);
    return thinking
      ? { role: "assistant", label: "Reasoning", text: thinking, sent: thinking }
      : encryptedReasoningPart(nonEmptyString(block.signature) ?? "");
  }
  if (type === "tool_use") {
    return {
      role: "assistant",
      label: `Calls ${nonEmptyString(block.name) ?? "a tool"}`,
      text: readableText(block.input),
      sent: argumentsText(block.input),
    };
  }
  if (type === "tool_result") {
    const output = typeof block.content === "string" ? block.content : contentText(block.content);
    const id = nonEmptyString(block.tool_use_id);
    if (id) toolResults.set(id, output);
    return { role: "tool", label: "Tool result", text: readableText(output), sent: output };
  }
  return {
    role: assistant ? "assistant" : "user",
    label: type,
    text: JSON.stringify(block, null, 2),
    sent: JSON.stringify(block),
  };
}

/**
 * Reads a whole message, or rebuilds it from the content blocks a stream sent. Model Studio's own
 * errors are `{ code, message }` at the top of the body or a stream event, rather than Anthropic's
 * `error` object.
 */
function readReply(responseBody: string): ModelReply {
  const reply: ModelReply = {
    reasoning: "",
    reasoningEncryptedWithoutSummary: false,
    text: "",
    toolCalls: [],
    finishReasons: [],
    nativeFinishReasons: [],
  };
  const blocks = new Map<number, Record<string, unknown>>();
  const toolInputs = new Map<number, string>();
  let stopReason: string | undefined;
  for (const event of jsonObjects(responseBody)) {
    const message = isRecord(event.message) ? event.message : event;
    if (message.type === "message" || event.type === "message_start") {
      reply.id = nonEmptyString(message.id) ?? reply.id;
      reply.model = nonEmptyString(message.model) ?? reply.model;
      for (const [index, block] of records(message.content).entries()) blocks.set(index, block);
    }
    if (isRecord(event.error)) {
      reply.errorType = errorCode(event.error);
      reply.errorMessage = nonEmptyString(event.error.message);
    } else if (nonEmptyString(event.code) && reply.errorType === undefined) {
      reply.errorType = nonEmptyString(event.code);
      reply.errorMessage = nonEmptyString(event.message);
    }
    const delta = isRecord(event.delta) ? event.delta : {};
    stopReason =
      nonEmptyString(message.stop_reason) ?? nonEmptyString(delta.stop_reason) ?? stopReason;
    const index = finiteNumber(event.index);
    if (index === undefined) continue;
    if (event.type === "content_block_start" && isRecord(event.content_block))
      blocks.set(index, { ...event.content_block });
    if (event.type !== "content_block_delta") continue;
    const block = blocks.get(index) ?? {};
    blocks.set(index, block);
    if (delta.type === "text_delta") block.text = `${block.text ?? ""}${delta.text ?? ""}`;
    if (delta.type === "thinking_delta")
      block.thinking = `${block.thinking ?? ""}${delta.thinking ?? ""}`;
    if (delta.type === "signature_delta") block.signature = delta.signature;
    if (delta.type === "input_json_delta")
      toolInputs.set(index, `${toolInputs.get(index) ?? ""}${delta.partial_json ?? ""}`);
  }
  let encrypted = false;
  for (const [index, block] of [...blocks.entries()].sort(([left], [right]) => left - right)) {
    if (block.type === "text") reply.text += nonEmptyString(block.text) ?? "";
    else if (block.type === "thinking") {
      const thinking = nonEmptyString(block.thinking);
      if (thinking) reply.reasoning += (reply.reasoning ? "\n\n" : "") + thinking;
      else encrypted = true;
    } else if (block.type === "tool_use")
      reply.toolCalls.push(toolCallOf(block, toolInputs.get(index)));
  }
  reply.reasoningEncryptedWithoutSummary = encrypted && !reply.reasoning;
  if (stopReason) {
    reply.finishReasons = [finishReason(stopReason)];
    reply.nativeFinishReasons = [stopReason];
  }
  return reply;
}

function toolCallOf(
  block: Record<string, unknown>,
  streamedInput: string | undefined,
): ModelToolCall {
  let input: unknown = block.input;
  if (streamedInput) {
    try {
      input = JSON.parse(streamedInput);
    } catch {
      input = streamedInput;
    }
  }
  return {
    id: nonEmptyString(block.id),
    name: nonEmptyString(block.name) ?? "a tool",
    arguments: input,
  };
}

function readChunks(responseBody: string): StreamChunk[] {
  const toolNames = new Map<number, string>();
  return jsonObjects(responseBody).map((event): StreamChunk => {
    const index = finiteNumber(event.index) ?? 0;
    const delta = isRecord(event.delta) ? event.delta : {};
    if (event.type === "content_block_start" && isRecord(event.content_block)) {
      const block = event.content_block;
      if (block.type !== "tool_use") return { kind: "other" };
      const name = nonEmptyString(block.name) ?? "a tool";
      toolNames.set(index, name);
      return { kind: "tool", tool: name, call: String(index), text: "" };
    }
    if (event.type === "content_block_delta") {
      if (delta.type === "text_delta") return { kind: "text", text: String(delta.text ?? "") };
      if (delta.type === "thinking_delta")
        return { kind: "reasoning", text: String(delta.thinking ?? "") };
      if (delta.type === "signature_delta") return { kind: "reasoning", text: "" };
      if (delta.type === "input_json_delta")
        return {
          kind: "tool",
          tool: toolNames.get(index) ?? "a tool",
          call: String(index),
          text: String(delta.partial_json ?? ""),
        };
    }
    if (event.type === "message_delta")
      return delta.stop_reason ? { kind: "finish" } : { kind: "usage" };
    return { kind: "other" };
  });
}

function finishReason(reason: string): string {
  if (reason === "tool_use") return "tool_call";
  if (reason === "max_tokens") return "length";
  if (reason === "refusal") return "content_filter";
  return "stop";
}
